import { useState } from "react";
import { Alert } from "react-bootstrap";
import Select, { StylesConfig } from "react-select";
import moment from "moment";
import {
  Field,
  FieldType,
  FilterBar,
  FixedToolbarItem,
  FlexToolbarItem,
  IconButton,
  LinePlot,
  Toolbar,
  useAATheme,
  useModelColorMap,
  useReactSelectTheme,
} from "@bagel-web/components";
import { useQuery } from "@tanstack/react-query";

import type { CommonAPI, MetricUsage, UsageMetricMetadata } from "../types";
import { formatCurrency, formatNumber } from "../util";
import useFilters from "../filter/useFilters";
import { usageQueries } from "../queries";

const defaultStartDate = moment().subtract(1, "month").format("YYYY-MM-DD");
const defaultEndDate = moment().format("YYYY-MM-DD");
const fields: Array<Field> = [
  {
    name: "usageDetailStartDate",
    label: "Start Date",
    type: FieldType.Date,
    required: true,
    defaultValue: defaultStartDate,
  },
  {
    name: "usageDetailEndDate",
    label: "End Date",
    type: FieldType.Date,
    required: true,
    defaultValue: defaultEndDate,
  },
];

const getMetricValue = (usage: MetricUsage, metricKey: string) =>
  Number(usage.metrics[metricKey as keyof typeof usage.metrics] ?? 0);

type FieldOption = {
  value: string;
  label: string;
};

const fieldStyleOverrides: StylesConfig<FieldOption, false> = {
  container: (baseStyles) => ({
    ...baseStyles,
    minWidth: "180px",
  }),
};

const MetricUsageDetailReport = ({
  commonAPI,
  organization,
  projectId,
  organizationName,
  metrics,
  getCost,
  exportUsageDetailReport,
}: {
  commonAPI: CommonAPI;
  // `organization` + `projectId` identify the usage scope (projectId "" => the
  // organization aggregate) so the query is keyed and fetched per scope.
  organization: string;
  projectId: string;
  organizationName?: string;
  metrics: UsageMetricMetadata[];
  getCost?: (usage: MetricUsage) => number;
  exportUsageDetailReport?: (
    usage: MetricUsage[],
    startDate: string,
    endDate: string,
    organization?: string
  ) => void;
}) => {
  const [selectedField, setSelectedField] = useState<string>(
    metrics[0]?.key || "calls"
  );
  const { getColors } = useModelColorMap();
  const { filterCriteria, handleSetFilters, handleClearFilter } =
    useFilters(fields);

  const startDate = filterCriteria.usageDetailStartDate?.value || "";
  const endDate = filterCriteria.usageDetailEndDate?.value || "";

  const { isDarkMode } = useAATheme();

  const { data: usage } = useQuery({
    queryKey: [
      ...usageQueries.getUsageBetween({ organization, startDate, endDate }),
      projectId,
      selectedField,
    ],
    queryFn: () => {
      if (
        !filterCriteria.usageDetailStartDate?.value ||
        !filterCriteria.usageDetailEndDate?.value ||
        endDate < startDate
      ) {
        return [];
      }
      const inclusiveEndDate = moment(endDate)
        .add(1, "days")
        .format("YYYY-MM-DD");
      return projectId
        ? commonAPI.fetchProjectUsageBetween({
            organization,
            projectId,
            startDate,
            endDate: inclusiveEndDate,
          })
        : commonAPI.fetchOrganizationUsageBetween({
            organization,
            startDate,
            endDate: inclusiveEndDate,
          });
    },
  });

  const allModelNames = Array.from(
    new Set([...(usage?.map((item) => item.service_alias_name) || [])])
  );

  const datasets = usage
    ? allModelNames.map((modelName) => ({
        label: modelName,
        borderColor: getColors(modelName).borderColor,
        backgroundColor: getColors(modelName).backgroundColor,
        data: usage
          .filter((item) => item.service_alias_name === modelName)
          .map((item) => ({
            x: item.timestamp,
            y:
              selectedField === "cost" && getCost
                ? getCost(item)
                : getMetricValue(item, selectedField),
          })),
      }))
    : undefined;

  const fieldOptions: FieldOption[] = [
    ...metrics.map((metric) => ({
      value: metric.key,
      label: metric.display_name,
    })),
    ...(getCost ? [{ value: "cost", label: "Estimated Cost" }] : []),
  ];
  const selectedFieldOption = fieldOptions.find(
    (option) => option.value === selectedField
  );

  const formatValue = (value: string | number) =>
    selectedField === "cost"
      ? formatCurrency(Number(value))
      : formatNumber(Number(value));

  let linePlotOptions = LinePlot.getDefaultOptions({ isDarkMode });
  linePlotOptions = {
    ...linePlotOptions,
    scales: {
      ...linePlotOptions?.scales,
      x: {
        ...linePlotOptions?.scales?.x,
        type: "time",
        time: { unit: "day", tooltipFormat: "MMMM DD, YYYY" },
        suggestedMin: moment().subtract(1, "months").format("YYYY-MM-DD"),
        suggestedMax: moment().format("YYYY-MM-DD"),
      },
      y: {
        ...linePlotOptions?.scales?.y,
        ticks: {
          ...linePlotOptions?.scales?.y?.ticks,
          callback: (value) => formatValue(value),
        },
      },
    },
    plugins: {
      ...linePlotOptions?.plugins,
      legend: {
        ...linePlotOptions?.plugins?.legend,
        position: "right",
      },
      tooltip: {
        ...linePlotOptions?.plugins?.tooltip,
        callbacks: {
          ...linePlotOptions?.plugins?.tooltip?.callbacks,
          label: (tooltipItems) => {
            const value = (tooltipItems.raw as { y: number }).y;
            return formatValue(value);
          },
        },
      },
    },
  };

  return (
    <div>
      <Toolbar className="mb-3">
        <FlexToolbarItem>
          <Select<FieldOption, false>
            value={selectedFieldOption ?? null}
            options={fieldOptions}
            styles={fieldStyleOverrides}
            theme={useReactSelectTheme()}
            isClearable={false}
            isSearchable={false}
            onChange={(newValue) => {
              if (newValue) {
                setSelectedField(newValue.value);
              }
            }}
          />
        </FlexToolbarItem>

        <FlexToolbarItem>
          <FilterBar
            fields={fields}
            criteria={filterCriteria}
            onSetFilters={handleSetFilters}
            onClear={handleClearFilter}
          />
        </FlexToolbarItem>

        {exportUsageDetailReport && (
          <FixedToolbarItem>
            <IconButton
              disabled={
                !organizationName ||
                !usage ||
                usage.length === 0 ||
                startDate > endDate
              }
              size="lg"
              className="bi-download"
              tooltip="Download CSV"
              onClick={() =>
                usage &&
                exportUsageDetailReport(
                  usage,
                  startDate,
                  endDate,
                  organizationName
                )
              }
            />
          </FixedToolbarItem>
        )}
      </Toolbar>

      {startDate > endDate && (
        <Alert variant="warning">
          Please select an end date <b>on or after</b> the start date.
        </Alert>
      )}

      <LinePlot
        height="350px"
        data={datasets ? { datasets } : undefined}
        options={linePlotOptions}
      />
    </div>
  );
};

export default MetricUsageDetailReport;
